import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import { ListIcons } from 'components/Stage/utils';
import { SellingPoints } from 'components/Stage/SellingPoints/SellingPoints';
import { LoanSelectorSkeleton } from 'components/LoanSelector/LoanSelectorSkeleton';

import {
  HiddenOnDesktopBox,
  HiddenOnMobileBox,
} from 'components/VisibilityBoxes/VisibilityHelperBox';
import { PartnerPanels } from '../StageParts/PartnerPanels';

import { expandTextMacros } from 'lib/text-macros/textMacroHelper';

// Below-the-fold dynamic imports for better LCP
const LegalTextGoogle = dynamic(
  () =>
    import('components/Stage/LegalTextGoogle').then((m) => ({
      default: m.LegalTextGoogle,
    })),
  { ssr: true },
);

const LegalTextBestPriceGuarantee = dynamic(
  () =>
    import('components/Stage/LegalText').then((m) => ({
      default: m.LegalTextBestPriceGuarantee,
    })),
  { ssr: true },
);

import type {
  ISeaStageWithBannerFields,
  IStageBannerFields,
} from 'types/generated/contentful';
import type { AssetFields } from 'contentful';
import { useState, useEffect } from 'react';
import { getOsName } from 'lib/router/route-helper';
import useDecision from 'lib/helpers/optimizely/useDecision';
import { ABTestKeys, Variation } from 'lib/helpers/optimizely/flagConfigs';
import { getAppRedirectionExperimentStageConfig } from 'components/ABTestVariations/CAT3820AppRedirectionV4/helpers';
import { Overlay } from 'components/ABTestVariations/CAT3820AppRedirectionV4/Overlay';

const HEADLINE_DEFAULT = 'Günstige Kredite - garantiert!';

const LoanSelector = dynamic(
  () => import('components/LoanSelector').then((mod) => mod.LoanSelector),
  {
    loading: () => <LoanSelectorSkeleton />,
    ssr: true,
  },
);

const Headline = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 42,
  lineHeight: '44px',
  width: '100%',
  marginBottom: 0,
  letterSpacing: '-1px',

  [theme.breakpoints.up('md')]: {
    lineHeight: '72px',
    fontSize: 60,
    width: '90%',
  },
}));

const HeadlineGridItem = styled(Grid)(({ theme }) => ({
  padding: '0',
  height: 'auto',
  position: 'relative',
  [theme.breakpoints.up('md')]: { paddingLeft: '1%' },
}));

const PartnerPanelsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  overflowX: 'hidden',
  overflowY: 'hidden',
  gap: theme.spacing(2),
  height: '100px',

  marginTop: 0,
  marginBottom: 0,

  // hide scrollbar but keep functionality
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },

  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(2),
    justifyContent: 'center',
    flexWrap: 'wrap',
    height: 'auto',
    minHeight: '100px',
  },
}));

const LegalTextWrapper = styled('div')(({ theme }) => ({
  paddingRight: 0,
  width: '100%',
  a: {
    color: `${theme.palette.grey[600]} !important`,
    textDecorationColor: `${theme.palette.grey[600]} !important`,
  },

  [theme.breakpoints.up('md')]: {
    paddingRight: theme.spacing(2),
  },
}));

const LpContainer = styled(Container)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const BannerImageWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '532px',
  marginTop: `${theme.spacing(3)}`,
  marginBottom: `${theme.spacing(1)}`,
  position: 'relative',

  [theme.breakpoints.down('md')]: {
    maxWidth: '500px',
    marginTop: 0,
  },
}));

export type SeaStageWithBannerProps = ISeaStageWithBannerFields;

export function SeaStageWithBanner(props: SeaStageWithBannerProps) {
  const {
    headline,
    bulletPoints,
    bannerImage,
    partners,
    showCreditcardOption,
    ctaText,
    additionalParams,
  } = props;

  const listIcon = ListIcons.CHECK_MARKS;
  const headlineWithMacros = expandTextMacros(headline);

  // Extract banner image properties with proper type handling
  const bannerFields = bannerImage?.fields as IStageBannerFields | undefined;
  const bannerImageAsset = bannerFields?.image;
  const assetFields = bannerImageAsset?.fields as AssetFields | undefined;
  const bannerImageUrl = assetFields?.file?.url;
  const bannerImageAltText = assetFields?.description || 'Banner';
  const bannerImageWidth = assetFields?.file?.details?.image?.width || 350;
  const bannerImageHeight = assetFields?.file?.details?.image?.height || 200;

  const [deviceOS, setDeviceOS] = useState('unknown');

  useEffect(() => {
    setDeviceOS(getOsName());
  }, []);

  // Mobile detection for AB test - deviceOS initialises to 'unknown' on SSR
  // and updates client-side to avoid hydration mismatch

  const overrideAttributes = {
    overrideAttributes: {
      deviceOS,
    },
  };

  const [decisionCAT4168AppRedirectionV5] = useDecision(
    ABTestKeys.CAT_4600_APP_REDIRECTION_V3,
    undefined,
    { ...overrideAttributes },
  );

  const appRedirectionVariation =
    decisionCAT4168AppRedirectionV5.variationKey || null;

  const appRedirectionExperimentConfig = getAppRedirectionExperimentStageConfig(
    appRedirectionVariation || '',
  );

  return (
    <Box
      sx={{
        background:
          'linear-gradient(0deg, #f7f7f7 0%, rgba(247, 247, 247, 0) 100%)',
        padding: { xs: '24px 0 16px', md: '64px 0 32px' },
      }}
    >
      <LpContainer>
        <Grid container>
          {appRedirectionVariation &&
            appRedirectionExperimentConfig.isOverlayEnabled && (
              <Overlay variation={appRedirectionVariation as Variation} />
            )}
          <HeadlineGridItem size={{ xs: 12, md: 6 }}>
            <Headline variant="h1" suppressHydrationWarning>
              {headlineWithMacros || HEADLINE_DEFAULT}
            </Headline>
            <HiddenOnMobileBox>
              <SellingPoints sellingPoints={bulletPoints} listIcon={listIcon} />
              {bannerImageUrl && (
                <BannerImageWrapper>
                  <Image
                    src={`https:${bannerImageUrl}`}
                    alt={bannerImageAltText}
                    width={bannerImageWidth}
                    height={bannerImageHeight}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxWidth: '350px',
                    }}
                  />
                </BannerImageWrapper>
              )}

              <LegalTextWrapper>
                <LegalTextBestPriceGuarantee />
              </LegalTextWrapper>
            </HiddenOnMobileBox>
          </HeadlineGridItem>
          <Grid
            size={{ xs: 12 }}
            sx={{
              display: { xs: 'block', md: 'none' },
              textAlign: 'center',
            }}
          >
            <SellingPoints sellingPoints={bulletPoints} listIcon={listIcon} />

            {bannerImageUrl && (
              <BannerImageWrapper>
                <Image
                  src={`https:${bannerImageUrl}`}
                  alt={bannerImageAltText}
                  width={bannerImageWidth}
                  height={bannerImageHeight}
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                />
              </BannerImageWrapper>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <LoanSelector
              ctaText={ctaText}
              hideTvBanner
              isCreditCardCategoryEnabled={showCreditcardOption}
              additionalParams={additionalParams}
              appRedirectionVariation={appRedirectionVariation}
            />
          </Grid>
          <HiddenOnDesktopBox sx={{ width: '100%' }}>
            <PartnerPanelsWrapper>
              <PartnerPanels partners={partners} />
            </PartnerPanelsWrapper>

            <LegalTextWrapper>
              <LegalTextBestPriceGuarantee />
              <LegalTextGoogle />
            </LegalTextWrapper>
          </HiddenOnDesktopBox>
        </Grid>
      </LpContainer>
    </Box>
  );
}
